import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from '~/config/firebase.config';

export type CouponStatus = 'sent' | 'opened' | 'validated';

export type CouponDoc = {
    id: string;
    playerId: string;
    offerId: string;
    offerName: string;
    issuedAt: Timestamp;
    validUntil: Timestamp;
    gameId: string;
    obtainedScore: number;
    leaderboardScope: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all-time' | 'participation';
    status: CouponStatus;
    clientName?: string;
};

const getCouponsRef = (clientID: string) => collection(db, 'clients', clientID, 'coupons');

export const CouponsAPI = {
    ref: getCouponsRef,
    async create(clientID: string, data: Omit<CouponDoc, 'issuedAt' | 'id'> & { issuedAt?: Timestamp }) {
        const col = getCouponsRef(clientID);
        const ref = doc(col);
        const { id } = ref;
        const issuedAt = data.issuedAt ?? serverTimestamp();
        await setDoc(ref, { ...data, id, issuedAt });
        return id;
    },
    async updateStatus(clientID: string, couponId: string, status: CouponStatus) {
        const ref = doc(getCouponsRef(clientID), couponId);
        await updateDoc(ref, { status });
    },
    async getById(clientID: string, couponId: string): Promise<CouponDoc | null> {
        const ref = doc(getCouponsRef(clientID), couponId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
            return { id: snap.id, ...(snap.data() as any) } as CouponDoc;
        }
        return null;
    },
    async existsTodayForScope(
        clientID: string,
        playerId: string,
        scope: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all-time' | 'participation',
        startOfDay: Date,
        endOfDay: Date,
        gameId: string
    ): Promise<boolean> {
        const ref = getCouponsRef(clientID);
        const startOfDayTimestamp = Timestamp.fromDate(startOfDay);
        const endOfDayTimestamp = Timestamp.fromDate(endOfDay);
        const q = query(
            ref,
            where('playerId', '==', playerId),
            where('leaderboardScope', '==', scope),
            where('gameId', '==', gameId),
            where('issuedAt', '>=', startOfDayTimestamp),
            where('issuedAt', '<', endOfDayTimestamp),
            limit(1)
        );
        const snap = await getDocs(q);
        return !snap.empty;
    },
    async existsAnyToday(clientID: string, playerId: string, startOfDay: Date, endOfDay: Date): Promise<boolean> {
        const ref = getCouponsRef(clientID);
        const startOfDayTimestamp = Timestamp.fromDate(startOfDay);
        const endOfDayTimestamp = Timestamp.fromDate(endOfDay);
        const q = query(
            ref,
            where('playerId', '==', playerId),
            where('issuedAt', '>=', startOfDayTimestamp),
            where('issuedAt', '<', endOfDayTimestamp),
            limit(1)
        );
        const snap = await getDocs(q);
        return !snap.empty;
    },
    async getLatest(clientID: string, count: number = 1) {
        const ref = getCouponsRef(clientID);
        const q = query(ref, orderBy('issuedAt', 'desc'), limit(count));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    },
};
